import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../shared/themes/redesignTokens";
import {
  GuidanceCategory,
  guidanceCategoryColors,
} from "../utils/guidanceCategories";

// ═══════════════════════════════════════════════════════════════
//  ContextGuidanceBox — color-coded guidance box with 6 categories
//  Replaces the previous "everything is blue" guidance box.
// ═══════════════════════════════════════════════════════════════

interface ContextGuidanceBoxProps {
  /** The guidance text to display */
  text: string;
  /** Optional name to render in bold within the text */
  boldName?: string;
  /** Semantic color category */
  category: GuidanceCategory;
}

/**
 * GuidanceTextRenderer — makes a name bold within the text.
 * Extracted so it can be reused in both OfferCard and ServiceRequestCard.
 */
const GuidanceTextRenderer: React.FC<{ text: string; boldName?: string }> = ({
  text,
  boldName,
}) => {
  if (!boldName || !text.includes(boldName)) {
    return <>{text}</>;
  }
  const idx = text.indexOf(boldName);
  const before = text.slice(0, idx);
  const after = text.slice(idx + boldName.length);
  return (
    <>
      {before}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {boldName}
      </Box>
      {after}
    </>
  );
};

const ContextGuidanceBox: React.FC<ContextGuidanceBoxProps> = ({
  text,
  boldName,
  category,
}) => {
  const colors = guidanceCategoryColors[category];

  return (
    <Box
      sx={{
        padding: "10px 14px",
        backgroundColor: colors.bg,
        borderRadius: dt.radiusSm,
        borderLeft: `3px solid ${colors.border}`,
        transition: "all 0.2s ease",
      }}
    >
      <Box
        sx={{
          fontSize: "13px",
          fontFamily: dt.font,
          color: colors.text,
          fontWeight: 600,
          lineHeight: 1.4,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        <GuidanceTextRenderer text={text} boldName={boldName} />
      </Box>
    </Box>
  );
};

export default ContextGuidanceBox;
export { GuidanceTextRenderer };
